import React from "react";
import moment from "moment";
import './CssFiles/indexcss.css'

const statusBucket = (app) => {
  // Prefer explicit agendatab, fall back to status name
  const raw =
    app?.appointmentStatus?.agendatab ||
    app?.appointmentStatus?.name ||
    "Pending";
  return String(raw).replace(/ Items$/, "");
};

const fmtTimeUntil = (appDate, startTime, durationMin = 60) => {
  const now = moment();
  const start = moment(`${appDate} ${startTime}`, "YYYY-MM-DD HH:mm");
  if (!start.isValid()) return "Invalid date";
  const end = start.clone().add(durationMin, "minutes");

  if (now.isAfter(end)) return "Session Completed";
  if (now.isAfter(start)) return "Session already started";

  const dur = moment.duration(start.diff(now));
  const d = Math.floor(dur.asDays());
  const h = dur.hours();
  const m = dur.minutes();
  const parts = [];
  if (d) parts.push(`${d} day${d !== 1 ? "s" : ""}`);
  if (h) parts.push(`${h} hour${h !== 1 ? "s" : ""}`);
  parts.push(`${m} min`);
  return `Starts in ${parts.join(", ")}`;
};

export default function AgendaView({
  currentView,
  currentAgendaTab,
  setCurrentAgendaTab,
  appointments,
  miniSelectedDate,
}) {
  if (currentView !== "agenda") return null;

  // Filter by date (YYYY-MM-DD)
  let filtered = Array.isArray(appointments) ? [...appointments] : [];
  if (miniSelectedDate) {
    filtered = filtered.filter((a) => a.date?.startsWith(miniSelectedDate));
  }

  // Filter by tab
  filtered = filtered.filter((a) => statusBucket(a) === currentAgendaTab);

  // Sort by date+start
  filtered.sort(
    (a, b) =>
      new Date(`${a.date}T${a.startTime || a.start || "00:00"}`) -
      new Date(`${b.date}T${b.startTime || b.start || "00:00"}`)
  );

  // Group by date
  const groups = filtered.reduce((acc, a) => {
    const key = (a.date || "Unknown").split("T")[0];
    (acc[key] ||= []).push(a);
    return acc;
  }, {});

  // Tab counts
  const counts = (["Scheduled", "Finished", "Pending"]).reduce((acc, k) => {
    acc[k] = (appointments || []).filter((a) => statusBucket(a) === k).length;
    return acc;
  }, {});

  return (
    <div className="agenda-root">
      {/* Tabs */}
      <div className="agenda-tabs_CalendarView">
        {["Scheduled", "Finished", "Pending"].map((tab) => (
          <button
            key={tab}
            type="button"
            className={`agenda-tab_CalendarView ${currentAgendaTab === tab ? "active" : ""}`}
            onClick={() => setCurrentAgendaTab(tab)}
          >
            <span>{tab}</span>
            <span className="agenda-tab-count">{counts[tab] || 0}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="agenda-view-container">
        {Object.keys(groups).length === 0 ? (
          <div className="agenda-empty">No appointments found.</div>
        ) : (
          Object.keys(groups).map((date) => (
            <div className="agenda-date-group" key={date}>
              <div className="agenda-date-header">
                <div className="agenda-date-title">
                  {moment(date, "YYYY-MM-DD").format("dddd, MMM D, YYYY")}
                </div>
              </div>

              <div className="agenda-items">
                {groups[date].map((app) => {
                  const time = app.startTime || app.start || "00:00";
                  const statusName = app?.appointmentStatus?.name || "Pending";
                  const statusKey = statusBucket(app).toLowerCase(); // scheduled|finished|pending
                  const duration = parseInt(app.Duration || 60, 10);

                  return (
                    <div className="agenda-item" key={app._id || `${date}-${time}`}>
                      <div className="agenda-time">
                        <div className="agenda-time-main">{time}</div>
                        <div className={`agenda-status status--${statusKey}`}>
                          {statusName}
                        </div>
                      </div>

                      <div className="agenda-details">
                        <div className="agenda-line">
                          <strong>Client:</strong>{" "}
                          {app.patientRequestId?.firstName} {app.patientRequestId?.lastName}
                        </div>
                        <div className="agenda-line">
                          <strong>Provider:</strong>{" "}
                          {app.doctorRequestId?.firstName} {app.doctorRequestId?.lastName}
                        </div>
                        <div className="agenda-line">
                          <strong>Visit Reason:</strong> {app.visitReason?.visitReason || "N/A"}
                          {" · "}
                          <strong>Type:</strong> {app.deliveryMode?.name || "N/A"}
                        </div>
                        <div className="agenda-timer">
                          {fmtTimeUntil(date, time, duration)}
                        </div>
                      </div>

                      <div className="agenda-actions">
                        {String(app.deliveryMode?.name).trim().toLowerCase() === "telehealth" && (
                          <button type="button" className="join-telehealth-btn">
                            Join Telehealth
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
